"use client";

import { useCallback, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import log from "logbeacon";
import {
  groupReportsByDate,
  transformAgentReport,
  type ReportTimelineItem,
  type ReportsTimelineGroupedData,
} from "../utils/reportTimeline";
import { I18nUtil } from "@utils";

import { QUERY_KEYS } from "@/src/config/constant";
import type {
  AgentReportCategoryEnum,
  AgentReportPagination,
  AgentReport,
} from "@/src/features/api/memberApi";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";

// const DEFAULT_AGENT_IDS = ["1", "102"];
const DEFAULT_LIMIT = 20;

export interface UseReports360TimelineOptions {
  agentIds?: Array<string | number>;
  language?: string;
  limit?: number;
  enabled?: boolean;
  category?: AgentReportCategoryEnum;
}

export interface UseReports360TimelineResult {
  data: ReportsTimelineGroupedData;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  error: Error | null;
  refetch: () => void;
  loadMore: () => void;
}

export const useReports360Timeline = (
  options: UseReports360TimelineOptions = {},
): UseReports360TimelineResult => {
  const { memberApi } = useOspApiClientProvider();
  const { locale } = useParams<{ locale: string }>();

  const agentIds = useMemo(
    () => (options.agentIds || []).map((id) => String(id)).filter(Boolean),
    [options.agentIds],
  );

  const limit = options.limit ?? DEFAULT_LIMIT;
  const enabled =
    (options.enabled ?? true) && !!memberApi && agentIds.length > 0;
  const language =
    options.language ?? I18nUtil.getBackgroundLanguageKey(locale);

  const normalizePage = useCallback(
    (page?: AgentReportPagination | null): AgentReportPagination => ({
      limit: page?.limit,
      total: page?.total,
      next_token: page?.next_token,
      rows: page?.rows ?? [],
    }),
    [],
  );

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  } = useInfiniteQuery<AgentReportPagination>({
    queryKey: [
      QUERY_KEYS.AGENT_REPORTS_TIMELINE,
      agentIds.join(","),
      language,
      limit,
      options.category,
    ],
    enabled,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage?.next_token ?? null,
    queryFn: async ({ pageParam }) => {
      const nextToken =
        typeof pageParam === "string" && pageParam.length > 0
          ? pageParam
          : undefined;

      if (!memberApi) {
        return normalizePage();
      }

      try {
        const response = await memberApi.agents.listAgentReport({
          agent_ids: agentIds,
          language,
          limit,
          next_token: nextToken,
          category: options.category,
          "enrich.enable": true,
          "enrich.with_asset.enable": true,
          // "enrich.with_asset.with_price": true,
        } as any);
        return normalizePage(response?.data?.data);
      } catch (err) {
        log.error("[Reports360] Failed to load agent reports", err);
        throw err;
      }
    },
  });

  const groupedData = useMemo<ReportsTimelineGroupedData>(() => {
    if (!data?.pages) {
      return {};
    }
    const timelineItems: ReportTimelineItem[] = [];
    data.pages.forEach((page) => {
      page?.rows?.forEach((report: AgentReport) => {
        const mapped = transformAgentReport(report);
        if (mapped) {
          timelineItems.push(mapped);
        }
      });
    });
    return groupReportsByDate(timelineItems);
  }, [data]);

  const loadMore = useCallback(() => {
    if (hasNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  return {
    data: groupedData,
    isLoading,
    isFetchingNextPage,
    hasNextPage: Boolean(hasNextPage),
    error: (error as Error) ?? null,
    refetch,
    loadMore,
  };
};
